<script lang="ts">
	//Svelte
	import { beforeUpdate } from 'svelte';
	import { createEventDispatcher } from 'svelte';
	//Services
	import { postReq } from '../../../services/http';
	import { format } from 'svelte-i18n';
	//Stores
	import { scenes } from 'src/stores/lists/scenes';
	import { alarms } from 'src/stores/lists/scenes-derived';
	//Types
	import {
		ALARM_TIME_TRIGGER_DEFAULT,
		type IAlarmZone,
	} from 'src/components/AlarmZones/AlarmWizard/alarm_object';
	import type { DeviceButtonPropsTypes } from '@Shelly/shelly-design-system';
	//Helpers
	import { _deepClone } from '../../../helpers/utilities';
	//Design Sytem components
	import { DeviceButton, ActionsContainer, toast, DeviceButtonV2 } from '@Shelly/shelly-design-system';
	//Components
	import PinUnlock from 'src/components/PinUnlock.svelte';
	import { hasAlarmControlRights } from 'src/helpers/user_helpers';
	import { getAlarmGroupedDevices } from '../list-helpers';
	import AlarmBreachedSensorModal from './components/AlarmBreachedSensorModal.svelte';
	import AlarmDelayTimeCountdown from './components/AlarmDelayTimeCountdown.svelte';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { isActiveHomeShared } from 'src/helpers/houses/homes_helpers';
	import SharedHomeOwnerManagedModal from 'src/components/SharedHome/SharedHomeOwnerManagedModal.svelte';
	import { homes } from 'src/stores';

	export let item: IAlarmZone = {};
	export let isOnWidget = false;
	export let isOnDrawer = false;
	export let isOnDashWidget = false;

	const dispatchAlarmDelayStart = createEventDispatcher<{
		alarmDelayStart: {};
	}>();
	const dispatchAlarmDelayEnd = createEventDispatcher<{ alarmDelayEnd: {} }>();
	let showPinLockModal = false;
	let timer: DeviceButtonPropsTypes['timer'] = {
		time: undefined,
		percentage: undefined,
	};
	let timerStarted = 0;
	let showBreachedModal = false;
	let showSharedManagedModal = false;
	let time: number;

	const handleAlarmUpdateRequest = async (alarm: IAlarmZone) => {
		try {
			const res = await postReq('/scene/edit', {
				id: alarm._id,
				scene_script: JSON.stringify(alarm),
			});
			if (res.isok) {
				scenes.editScene(alarm);
				// Show arm alarm toast, only for alarms without delay
				if (alarm._enabled && !alarm._enable_delay) {
					toast.open({
						type: 'success',
						title: $format('label_alarm_armed_success', {
							default: 'Alarm armed successfully!',
						}),
						text: `${alarm._meta.name} ${$format('label_alarm_active_monitoring', { default: 'is now active and monitoring.' })}`,
						isTitleBold: true,
					});
				}
			}
		} catch (error) {
			console.log('error on arm/disarm ', error);
			toast.open({
				type: 'danger',
				title: $format('error_global'),
			});
		}
	};

	const hasAlarmRights = hasAlarmControlRights(item);

	const handleLocked = (id: IAlarmZone['id']) => {
		if (item.pin_lock) {
			showPinLockModal = true;
		} else {
			handleUnlock(id);
		}
	};

	const handleBreached = () => {
		showBreachedModal = true;
	};

	const handleArm = async (id) => {
		const alarmGroupedDevices = getAlarmGroupedDevices(item);
		if (
			(alarmGroupedDevices?.breached?.length || alarmGroupedDevices?.offline?.length) &&
			!item._enabled
		) {
			handleBreached();
		} else {
			handleLocked(id);
		}
	};

	async function handleUnlock(id: CustomEvent | IAlarmZone['id']) {
		if (isSharedHomeActive) {
			showPinLockModal = false;
			showBreachedModal = false;
			showSharedManagedModal = true;
			return;
		}

		let _id = typeof id !== 'string' ? item._id : id;
		let edited = _deepClone($scenes[_id]) as IAlarmZone;
		edited._id = _id;
		edited._enabled = !edited?._enabled;

		if (edited._enabled && edited._enable_delay) {
			edited._meta._time_trigger = Date.now();
			dispatchAlarmDelayStart('alarmDelayStart', edited);
		} else {
			edited._meta._time_trigger = ALARM_TIME_TRIGGER_DEFAULT;
			timer = { time: undefined, percentage: undefined };
		}

		await handleAlarmUpdateRequest(edited);
	}

	function handleClose() {
		showPinLockModal = false;
	}

	const calcAlarmDelayTimer = (startTime, delay) => {
		let now = Date.now();
		const timeDiff = Math.max(0, startTime + delay - now);
		const percentage = (timeDiff / delay) * 100;
		const time = Math.round(timeDiff / 1000);
		return { time, percentage };
	};

	const isAlarmDelay = (alarm: IAlarmZone) => {
		const currentAlarm = $alarms[alarm.id];
		if (!currentAlarm) return false;

		return (
			currentAlarm._enabled && currentAlarm?._meta?._time_trigger && !!currentAlarm?._enable_delay
		);
	};

	const isAlarmDelayFinished = (alarm: IAlarmZone) => {
		const currentAlarm = $alarms[alarm.id];
		if (!currentAlarm) return false;

		const isAlarmDelayTimePassed =
			currentAlarm._meta._time_trigger + currentAlarm._enable_delay - Date.now() <= 0;

		return (
			currentAlarm?._meta?._time_trigger && currentAlarm?._enable_delay && isAlarmDelayTimePassed
		);
	};

	const handleTimerStart = (alarm: IAlarmZone) => {
		const currentAlarm = $alarms[alarm.id];
		if (timerStarted === currentAlarm._meta._time_trigger) {
			return;
		}
		timer = calcAlarmDelayTimer(currentAlarm._meta._time_trigger, currentAlarm._enable_delay);
		timerStarted = currentAlarm._meta._time_trigger;
	};

	const handleTimerEnd = async (alarm: IAlarmZone) => {
		const currentAlarm = $alarms[alarm.id];
		if (!currentAlarm._meta._time_trigger) {
			return;
		}

		timerStarted = 0;
		currentAlarm._meta._time_trigger = ALARM_TIME_TRIGGER_DEFAULT;
		dispatchAlarmDelayEnd('alarmDelayEnd', currentAlarm);
		await handleAlarmUpdateRequest(currentAlarm);
	};

	const getDeviceButtonProps = (
		alarm: IAlarmZone,
		timer: DeviceButtonPropsTypes['timer'],
		hasTimer: boolean,
	): Partial<DeviceButtonPropsTypes> => {
		return {
			icon: 'fas fa-shield',
			variant: 'primary',
			isActive: !!alarm._enabled,
			indicator: hasTimer ? null : alarm._enabled ? 'on' : 'off',
			timer,
			hasTimer,
		};
	};

	const handleDeviceButtonClick = (event: MouseEvent | TouchEvent): void => {
		event.stopPropagation();

		if (isSharedHomeActive) {
			showSharedManagedModal = true;
			return;
		}

		handleArm(item._id);
	};

	beforeUpdate(() => {
		if (isAlarmDelay(item)) {
			handleTimerStart(item);
		}

		if (isAlarmDelayFinished(item)) {
			handleTimerEnd(item);
		}
	});

	$: hasTimer = timerStarted !== 0 && item?._meta?._time_trigger && !!item?._enable_delay && !!time;

	$: isSharedHomeActive = isActiveHomeShared($homes.list);
	$: deviceButtonProps = getDeviceButtonProps(item, timer, hasTimer);
</script>

<AlarmDelayTimeCountdown alarm={item} bind:time />

{#if isOnWidget}
	{#if $showNewLayout}
		<DeviceButtonV2
			id="devicebutton-mr39yz7"
			aria-label={$format('label_device_button', { default: 'Device button' })}
			slot="first"
			{...deviceButtonProps}
			on:click={handleDeviceButtonClick}
		/>
	{:else}
		<DeviceButton
			id="devicebutton-mr39yz7"
			aria-label={$format('label_device_button', { default: 'Device button' })}
			slot="first"
			{...deviceButtonProps}
			on:click={handleDeviceButtonClick}
		/>
	{/if}
{:else if isOnDrawer}
	{#if $showNewLayout}
		<DeviceButtonV2
			id="devicebutton-fvyz6oy"
			class="justify-content-center"
			aria-label={$format('label_device_button', { default: 'Device button' })}
			{...deviceButtonProps}
			isWide
			size="l"
			on:click={handleDeviceButtonClick}
		/>
	{:else}
		<DeviceButton
			id="devicebutton-fvyz6oy"
			class="justify-content-center"
			aria-label={$format('label_device_button', { default: 'Device button' })}
			{...deviceButtonProps}
			isWide
			size="l"
			on:click={handleDeviceButtonClick}
		/>
	{/if}
{:else if isOnDashWidget}
	<DeviceButtonV2
		size="s"
		id="devicebutton-mr39yz7"
		aria-label={$format('label_device_button', { default: 'Device button' })}
		{...deviceButtonProps}
		on:click={handleDeviceButtonClick}
	/>
{:else if $showNewLayout}
	<ActionsContainer orientation="vertical" slot="right" bgColor="color-base">
		<DeviceButtonV2
			id="devicebutton-fytbciz"
			aria-label={$format('label_device_button', { default: 'Device button' })}
			slot="first"
			{...deviceButtonProps}
			disabled={!hasAlarmRights && !isSharedHomeActive}
			on:click={handleDeviceButtonClick}
		/>
	</ActionsContainer>
{:else}
	<ActionsContainer orientation="horizontal">
		<DeviceButton
			id="devicebutton-fytbciz"
			aria-label={$format('label_device_button', { default: 'Device button' })}
			slot="first"
			{...deviceButtonProps}
			disabled={!hasAlarmRights && !isSharedHomeActive}
			on:click={handleDeviceButtonClick}
		/>
	</ActionsContainer>
{/if}

{#if showPinLockModal}
	<PinUnlock
		bind:showModal={showPinLockModal}
		on:close={handleClose}
		on:unlock={handleUnlock}
		on:cancel={() => (showPinLockModal = false)}
	/>
{/if}

{#if showBreachedModal}
	<AlarmBreachedSensorModal
		alarm={item}
		bind:showModal={showBreachedModal}
		on:armAlarm={() => handleLocked(item._id)}
	/>
{/if}

<SharedHomeOwnerManagedModal bind:showModal={showSharedManagedModal} context="control_alarm" />
